import React, { useEffect, useMemo, useState } from 'react';
import Configurator from './pages/Configurator.js';
import { FormProvider, useForm } from 'react-hook-form';
import { type DAYS, getDay, isoDate } from './pages/Calendar';
import {
  getSeason,
  getSeasonPackages,
  isDateBookable,
  packageKeysOutsideSeason,
} from './season';
import { padStart } from './utils';
import Modal, { type Calculation } from './pages/Modal';
import ContactData from './pages/ContactData';
import Payment from './pages/Payment';
import Success from './pages/Success';
import useFormPersist from 'react-hook-form-persist';

export interface AppData {
  drinkDesc: string;
  packagesDesc: string;
  restUrl: string;
  getUrl: string;
  startDesc: string;
  hanekenfaehrInfo: string;
  alter_hafenInfo: string;
  vehicles: {
    available: boolean;
    name: string;
  }[];
  packages: {
    name: string;
    optional: boolean;
    price: string;
  }[];
  //times: Array<{ time: `${number}${number}:${number}${number}` }>;
  times: null;
  days: Array<{
    day: (typeof DAYS)[number];
    times: Array<{
      time: `${number}${number}:${number}${number}`;
    }>;
  }>;

  studentDiscount: string;
  thursdayPrice: string;
  inWeekPrice: string;
  basePrice: string;

  drinkPlaceholder: string;

  /** Locations closed for Nov/Dec (BBQ-Optionen → Winter) */
  winterDisabledLocations?: Array<'hanekenfaehr' | 'alter_hafen'>;
  /** Locations opened for Jan–Mar (BBQ-Optionen → Winter) */
  janMarEnabledLocations?: Array<'hanekenfaehr' | 'alter_hafen'>;
  /** Usable BBQ donuts Nov–Mar, 10 persons each (BBQ-Optionen → Winter) */
  winterDonutCount?: number;
}

export interface BBQSubmission {
  personCount: number;
  isStudent: boolean;
  childrenCount: number;
  vehicleId: string;
  drinks: string;
  date: string;
  time: string;
  packages: Record<string, boolean>;
  contactData: {
    firstName: string;
    lastName: string;
    birthday: string;
    idNumber: string;
    street: string;
    zip: string;
    city: string;
    phone: string;
    email: string;
  };
  invoice: {
    company: string;
    firstName: string;
    lastName: string;
    street: string;
    zip: string;
    city: string;
  };
  foodChoice: string;
  useInvoice: boolean;
  payment: string;
  discountCode: string;
  userNotes: string;
}

function isoDateTime(date: Date) {
  return (
    padStart(date.getFullYear(), 4, '0') +
    '-' +
    padStart(date.getMonth() + 1, 2, '0') +
    '-' +
    padStart(date.getDate(), 2, '0') +
    ' ' +
    padStart(date.getHours(), 2, '0') +
    ':' +
    padStart(date.getMinutes(), 2, '0')
  );
}

export default function App() {
  const appData: AppData = (window as any).bbqData;

  const loc = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);

    const loc = (searchParams.get('loc') ?? 'hanekenfaehr').trim();

    if (['hanekenfaehr', 'alter_hafen'].includes(loc)) {
      return loc as 'hanekenfaehr' | 'alter_hafen';
    } else {
      return 'hanekenfaehr';
    }
  }, []);

  const methods = useForm<BBQSubmission>({
    shouldUseNativeValidation: true,
    get defaultValues() {
      let date = new Date();
      date.setHours(0, 0, 0, 0);

      // First day that is bookable for this location (season, winter closure)
      // and a configured weekday — also covers e.g. Dec 31st rolling over
      // into the closed January.
      while (
        !isDateBookable(date, loc, appData) ||
        !appData.days.some(({ day }) => day === getDay(date))
      ) {
        date.setDate(date.getDate() + 1);
      }

      return {
        vehicleId: appData.vehicles[0]?.name!,
        personCount: 10,
        childrenCount: 0,
        date: isoDate(date),
      };
    },
  });
  const { handleSubmit, watch, setValue, getValues } = methods;
  const season = getSeason(watch('date'));
  const [page, setPage] = useState(0);
  const [showIndicator, setShowIndicator] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const people = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);

    const queryPeople = Math.max(
      Math.min(parseInt(searchParams.get('personen') ?? '0'), 50),
      4
    );

    if (!Number.isNaN(queryPeople) && queryPeople) {
      return queryPeople;
    } else {
      return 10;
    }
  }, []);

  useFormPersist('000003e8-7a8b-21f0-8900-325096b39f47', {
    watch,
    setValue,
    storage: window.sessionStorage,
    exclude: ['baz'],
  });

  useEffect(() => {
    requestAnimationFrame(() => {
      scrollTo({
        top: 0,
        behavior: 'instant',
      });
    });

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', String(page));
    history.pushState({ page }, '', window.location.href);

    const listener = function (event: PopStateEvent) {
      if (
        typeof event.state === 'object' &&
        event.state !== null &&
        'page' in event.state &&
        typeof event.state['page'] === 'number'
      ) {
        setPage(event.state['page']);
      } else {
        setPage(0);
      }
    };

    addEventListener('popstate', listener);
    return () => removeEventListener('popstate', listener);
  }, [page]);

  // When the selected date crosses into another season, untick the packages of
  // the season we left — otherwise they'd stay set (hidden) and get billed.
  useEffect(() => {
    for (const key of packageKeysOutsideSeason(season, appData)) {
      if (getValues(`packages.${key}`)) {
        setValue(`packages.${key}`, false);
      }
    }
  }, [season]);

  useEffect(() => {
    watch((_, field) => {
      if (
        field.name &&
        (field.name === 'isStudent' || field.name.startsWith('packages'))
      ) {
        setShowIndicator(true);
        setTimeout(() => {
          setShowIndicator(false);
        }, 500);
      }
    });
  }, [watch]);

  const calculations = (() => {
    const calculations: Array<Calculation> = [];

    const vehicle = appData.vehicles.find((v) => v.name === watch('vehicleId'));
    if (!vehicle) return [];

    const date = watch('date');
    const isThursday = date ? new Date(date).getDay() === 4 : false;
    const isMoWe = date
      ? new Date(date).getDay() <= 3 && new Date(date).getDay() >= 1
      : false;

    const isStudent = watch('isStudent');
    const count = watch('personCount');
    // Mon–Wed Gruppenangebot is a summer-only offer
    const isEssen = season === 'summer' && watch('packages.essen');
    const boatCount = Math.ceil(count / 10);

    if (isEssen) {
      calculations.push({
        name: `Gruppenangebot x ${count}`,
        value: 44.9 * count,
      });
    } else {
      // Thursday discount
      if (isThursday) {
        calculations.push({
          name: vehicle.name + ' - Donnerstag' + ` x ${boatCount}`,
          value: +appData.thursdayPrice * boatCount,
        });

        // student discount
        if (isStudent) {
          calculations.push({
            name: 'Studenten-Rabatt',
            value: 0 - +appData.studentDiscount,
          });
        }
      } else if (isMoWe) {
        calculations.push({
          name: vehicle.name + ' - Mo-Mi' + ` x ${boatCount}`,
          value: +appData.inWeekPrice * boatCount,
        });
      } else {
        calculations.push({
          name: vehicle.name + ` x ${boatCount}`,
          value: +appData.basePrice * boatCount,
        });
      }

      // Packages of the selected date's season (admin list in summer, fixed
      // winter packages Nov–Feb — see season.ts)
      for (const pkg of getSeasonPackages(season, appData)) {
        const enabled = !!watch(`packages.${pkg.key}`);

        if (enabled)
          calculations.push({
            name:
              pkg.name + (pkg.perPerson ? ` x ${count}` : ` x ${boatCount}`),
            value: pkg.perPerson ? pkg.price * count : pkg.price * boatCount,
          });
      }
    }

    return calculations;
  })();

  const onSubmit = ({
    date,
    time,
    childrenCount,
    personCount,
    packages,
    drinks,
    isStudent,
    useInvoice,
    invoice,
    payment,
    vehicleId,
    discountCode,
    contactData,
    userNotes,
    foodChoice,
  }: BBQSubmission) => {
    setLoading(true);

    // convert to array — only packages of the booked date's season
    const otherSeasonKeys = packageKeysOutsideSeason(getSeason(date), appData);
    const packageList = Object.keys(packages)
      .filter((key) => !otherSeasonKeys.includes(key))
      .map((key) => packages[key])
      .filter((v) => v);

    // add sum
    const finalCalcs = [
      ...calculations,
      {
        name: 'Endpreis',
        value: Math.max(
          calculations.reduce((prev, curr) => prev + curr.value, 0),
          0
        ),
        highlight: true,
      } satisfies Calculation,
    ];

    fetch(appData.restUrl, {
      method: 'POST',
      body: JSON.stringify({
        name: contactData.firstName + ' ' + contactData.lastName,
        date: `${date} ${time}`,
        data: {
          contact: contactData,
          invoice: useInvoice ? invoice : {},
          personCount: Math.floor(personCount),
          childrenCount: Math.floor(childrenCount),
          // Drinks wish is a summer-only field (not offered in the Christmas program)
          drinks: getSeason(date) === 'summer' ? drinks : undefined,
          isStudent,
          vehicleId,
          discountCode,
          payment,
          packages: packageList,
          userNotes,
          foodChoice: packages.essen ? foodChoice : undefined,
          loc,
          deviceWidth: window.innerWidth + 'px',
          referrer: document.referrer ? document.referrer : 'Direct',
          calculations: finalCalcs.map(
            (c) =>
              `${c.name} : [${c.value.toFixed(2).replace('.', ',').replace('-', '- ')} €]`
          ),
        },
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(() => {
        setLoading(false);
        setPage(3);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <div className={'relative'}>
      <div
        className={
          'tw:pt-12 tw:px-4 tw:md:px-8 tw:md:pt-16 tw:mb-8 tw:mx-auto tw:max-w-[800px]'
        }>
        <FormProvider {...methods}>
          {(() => {
            switch (page) {
              case 0:
                return (
                  <Configurator
                    people={people}
                    onNext={() => setPage(1)}
                    calculations={calculations}
                    loc={loc}
                  />
                );
              case 1:
                return (
                  <ContactData
                    onNext={() => setPage(2)}
                    onPrev={() => setPage(0)}
                  />
                );
              case 2:
                return (
                  <Payment
                    loading={loading}
                    onNext={onSubmit}
                    onPrev={() => setPage(1)}
                  />
                );
              case 3:
                return <Success />;
            }
          })()}
        </FormProvider>
      </div>

      <Modal calculations={calculations} showIndicator={showIndicator} />
    </div>
  );
}
